import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Flame, HeartHandshake, Globe, EyeOff, Crown, ShieldCheck, Sparkles, Download } from "lucide-react";

const ARTICLES = [
  {
    icon: HeartHandshake,
    title: "I. Consent is sacred",
    body: "Nothing here happens without an enthusiastic yes. Desire is only beautiful when it is mutual. We honour every boundary, every pause, every 'not tonight' — no explanation required.",
  },
  {
    icon: Flame,
    title: "II. Desire without shame",
    body: "This is a judgement-free empire. Your fantasies, kinks, and curiosities are welcome here. We celebrate pleasure as a natural, human, adult joy — never something to apologise for.",
  },
  {
    icon: Globe,
    title: "III. All are welcome",
    body: "Every gender, every orientation, every body. The Empire has no borders and no single 'type'. If you're an adult who comes with respect, you belong at this table.",
  },
  {
    icon: EyeOff,
    title: "IV. Discretion is a promise",
    body: "What happens in the Empire stays in the Empire. We never screenshot, expose, or share another member without consent. Privacy is not a feature — it is our word.",
  },
  {
    icon: Crown,
    title: "V. Creators are royalty",
    body: "Those who share their art and allure are the beating heart of this world. They set their own terms, own their work, and are paid fairly. We protect and champion our creators.",
  },
  {
    icon: ShieldCheck,
    title: "VI. Safety above all",
    body: "Strictly 18+. Zero tolerance for anyone under age, for coercion, or for harm. We verify, we listen, and we act fast. A safe empire is a thriving empire.",
  },
];

export default function Charter() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative z-10" style={{ background: "#0A0A0A" }} data-testid="charter-page">
      {/* ambient orbs */}
      <div className="orb" style={{ width: 360, height: 360, top: -80, right: "6%", background: "rgba(212,175,55,0.35)", animation: "16s ease-in-out 0s infinite normal none running floatOrb" }} />
      <div className="orb" style={{ width: 220, height: 220, bottom: "10%", left: "8%", background: "rgba(184,134,11,0.28)", animation: "20s ease-in-out 0s infinite reverse none running floatOrb" }} />

      <div className="max-w-3xl mx-auto px-6 md:px-8 py-10 md:py-16 relative">
        <button data-testid="charter-back" onClick={() => navigate(-1)} className="text-[#A0A0A0] hover:text-white flex items-center gap-2 mb-10">
          <ArrowLeft size={20} /> Back
        </button>

        <a
          data-testid="charter-download"
          href="/empire-charter.pdf"
          download="Pleasure-Empire-Charter.pdf"
          className="absolute right-6 md:right-8 top-10 md:top-16 btn-outline-gold px-4 py-2 rounded-full text-sm inline-flex items-center gap-2">
          <Download size={15} /> Download PDF
        </a>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="text-center mb-16">
          <Flame className="text-[#D4AF37] mx-auto mb-4" size={44} />
          <div className="overline mb-3">Our founding manifesto</div>
          <h1 className="font-heading text-5xl md:text-6xl mb-6">The Empire Charter</h1>
          <p className="text-[#C7C7C7] text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            We built Pleasure Empire for the bold — a members-only world where adults of every
            kind meet without shame, connect without borders, and play without fear. These are the
            vows we live by. By entering, you become part of them.
          </p>
        </motion.div>

        {/* Articles */}
        <div className="space-y-5">
          {ARTICLES.map((a, i) => (
            <motion.div
              key={a.title}
              data-testid={`charter-article-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="pe-card glow-hover rounded-2xl p-6 md:p-7 flex gap-5">
              <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border border-[#D4AF37]/40 bg-[#D4AF37]/10">
                <a.icon className="text-[#D4AF37]" size={22} />
              </div>
              <div>
                <h2 className="font-heading text-2xl text-white mb-2">{a.title}</h2>
                <p className="text-sm text-[#B8B8B8] leading-relaxed">{a.body}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Seal / closing */}
        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="text-center mt-16 pt-10 border-t border-[#2A2A2A]">
          <Sparkles className="text-[#D4AF37] mx-auto mb-4" size={28} />
          <p className="font-heading text-2xl md:text-3xl gold-text mb-3">Long live the Empire.</p>
          <p className="text-sm text-[#A0A0A0] max-w-md mx-auto mb-8">
            Enter with respect, leave with stories. Your seat at the table awaits.
          </p>
          <button data-testid="charter-join-btn" onClick={() => navigate("/auth")}
            className="btn-gold px-10 py-4 rounded-full text-base">Join the Empire</button>
          <p className="text-xs text-[#6b6b6b] mt-8">Pleasure Empire · Strictly 18+ · Play safe, play consensual.</p>
        </motion.div>
      </div>
    </div>
  );
}
